import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Single source of truth for debug UI settings.
 * All context (frappeSiteName, projectId, user) lives here — persisted to localStorage.
 * Other stores and api.ts read from here via getSettings().
 */

// Required — populated from ui/.env.development or ui/.env.production by
// Next.js at build time, based on NODE_ENV.
const DEFAULT_API_BASE = process.env.NEXT_PUBLIC_KAEL_API_URL!

interface SettingsState {
  // Settings
  baseUrl: string
  authToken: string
  clientId: string       // tenant identifier — locked after login
  projectId: string
  frappeSiteName: string // sent as X-Frappe-Site-Name header with every request
  userId: string         // from auth.me() — locked after login
  fullName: string       // from auth.me() — locked after login

  // Actions
  setBaseUrl: (url: string) => void
  setAuthToken: (token: string) => void
  setClientId: (id: string) => void
  setProjectId: (id: string) => void
  setFrappeSiteName: (name: string) => void
  setUserId: (id: string) => void
  setFullName: (name: string) => void
  resetSettings: () => void
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set) => ({
      baseUrl: DEFAULT_API_BASE,
      authToken: '',
      clientId: '',
      projectId: '',
      frappeSiteName: '',
      userId: '',
      fullName: '',

      setBaseUrl: (url) => set({ baseUrl: url }),
      setAuthToken: (token) => set({ authToken: token }),
      setClientId: (id) => set({ clientId: id }),
      setProjectId: (id) => set({ projectId: id }),
      setFrappeSiteName: (name) => set({ frappeSiteName: name }),
      setUserId: (id) => set({ userId: id }),
      setFullName: (name) => set({ fullName: name }),
      resetSettings: () => set({
        baseUrl: DEFAULT_API_BASE,
        authToken: '',
        clientId: '',
        projectId: '',
        frappeSiteName: '',
        userId: '',
        fullName: '',
      }),
    }),
    {
      name: 'kael-settings',
      version: 8,
      migrate: (persisted: any, version: number) => {
        if (!persisted) return persisted
        // v7+: domain rebrand kai-dev.inkers.ai → app-dev.kael.build
        const remap = (v: string) =>
          v === 'kai-dev.inkers.ai' || v === 'kai.inkers.ai' ? 'app-dev.kael.build' : v
        if (persisted.frappeSiteName) persisted.frappeSiteName = remap(persisted.frappeSiteName)
        if (persisted.clientId) persisted.clientId = remap(persisted.clientId)
        // v8: nuke any baseUrl that mentions the old domain — let it fall
        // back to DEFAULT_API_BASE (driven by NEXT_PUBLIC_KAEL_API_URL).
        if (typeof persisted.baseUrl === 'string' && persisted.baseUrl.includes('kai-dev.inkers.ai')) {
          persisted.baseUrl = DEFAULT_API_BASE
        }
        return persisted
      },
    }
  )
)

/** Read settings from outside React (api.ts, utilities, etc.) */
export const getSettings = () => {
  const state = useSettingsStore.getState()
  return {
    baseUrl: state.baseUrl,
    authToken: state.authToken,
    clientId: state.clientId,
    projectId: state.projectId,
    frappeSiteName: state.frappeSiteName,
    userId: state.userId,
    fullName: state.fullName,
  }
}
